import React, { useState, useMemo } from "react";
import { Box, Typography, Card, Button, CircularProgress, TextField, InputAdornment, Tooltip } from "../app/material-framework/materialComponents";
import { CardContent, CardActions, Chip, Divider, Alert, Stack } from "@mui/material";
import { Search, History, ChatBubbleOutline, AccessTime, PlayArrow, Replay } from "@mui/icons-material";
const relativeDate = (dateString, includeTime = true) => {
  if (!dateString) return "";
  const localDate = new Date(dateString);
  if (!localDate) return "";
  const now = new Date();
  const todayMidnight = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const time = localDate.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });

  if (localDate >= todayMidnight) {
    return includeTime ? `Today at ${time}` : "Today";
  }

  const dateStr = localDate.toLocaleDateString([], {
    month: "short",
    day: "numeric",
    ...(localDate.getFullYear() !== now.getFullYear() && { year: "numeric" }),
  });
  return includeTime ? `${dateStr} ${time}` : dateStr;
};

function getReadTimeFromMessages(messages) {
  if (!messages?.length) return "1 min";
  const WPM = 225;
  const allText = messages
    .flatMap((m) => [
      m.question || "",
      (m.answer || "").replace(/<[^>]+>/g, " "),
    ])
    .join(" ");
  const wordCount = (allText.trim().match(/\S+/g) || []).length;
  const minutes = Math.max(1, Math.ceil(wordCount / WPM));
  return `${minutes} min`;
}

function getEarliestMessage(messages) {
  if (!messages?.length) return null;
  return messages.reduce((earliest, msg) => {
    const currentTime = new Date(msg.question_time)?.getTime() || Infinity;
    const earliestTime = new Date(earliest.question_time)?.getTime() || Infinity;
    return currentTime < earliestTime ? msg : earliest;
  }, messages[0]);
}

function getFirstQuestion(messages) {
  if (!messages?.length) return "Empty conversation";
  const earliest = getEarliestMessage(messages);
  return earliest?.question || "No question available";
}

function getStartedDate(conv) {
  const earliest = getEarliestMessage(conv.messages);
  return earliest?.question_time || conv.started_at;
}

function truncateText(text, maxLength = 90) {
  return text.length <= maxLength ? text : text.substring(0, maxLength) + "…";
}

export function ChatHistoryTab({ onOpenConversation, onRerunQuestion, conversations, loading, error, onRetry }) {
  const [search, setSearch] = useState("");

  const filtered = useMemo(() => {
    if (!conversations) return [];
    return conversations
      .filter((conv) => {
        const matchesSearch =
          !search.trim() ||
          getFirstQuestion(conv.messages).toLowerCase().includes(search.toLowerCase());
        return matchesSearch;
      })
      .sort((a, b) => {
        const dateA = new Date(a.last_activity);
        const dateB = new Date(b.last_activity);
        return (dateB?.getTime() || 0) - (dateA?.getTime() || 0);
      });
  }, [conversations, search]);

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", height: "400px" }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 2 }}>
        <Alert severity="error" onClose={onRetry}>{error}</Alert>
        <Button variant="contained" onClick={onRetry} sx={{ mt: 2 }}>Retry</Button>
      </Box>
    );
  }

  return (
    <Box sx={{ flex: 1, overflowY: "auto", p: 2, bgcolor: "#F5F5F5" }}>
      <TextField
        placeholder="Search conversations..."
        size="small"
        fullWidth
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
          <Search sx={{ fontSize: 18, color: "#999" }} />
            </InputAdornment>
          ),
        }}
        sx={{ bgcolor: "#fff", borderRadius: 2, mb: 1.5, "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
      />

      {filtered.length === 0 && (
        <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", py: 8, gap: 1 }}>
          <History sx={{ fontSize: 40, color: "#ccc" }} />
          <Typography variant="body2" color="text.secondary">
            {conversations?.length === 0
              ? "No conversations yet. Start a new chat!"
              : "No conversations match your search."}
          </Typography>
        </Box>
      )}

      {filtered.map((conv) => {
        const title = truncateText(getFirstQuestion(conv.messages), 60);
        const earliest = getEarliestMessage(conv.messages);
        const preview = truncateText(earliest?.answer?.replace(/<[^>]+>/g, "") || "", 90);
        return (
          <Card
            key={conv.conv_id}
            elevation={0}
            sx={{
              border: "1px solid #E8E8E8",
              borderLeft: "3px solid #CA2920",
              borderRadius: 2,
              bgcolor: "#fff",
              mb: 1.25,
              mx: 1.5,
              transition: "box-shadow 0.2s",
              "&:hover": { boxShadow: "0 4px 16px rgba(0,0,0,0.08)" },
            }}
          >
            <CardContent sx={{ pt: 2, pb: "10px !important", px: 2 }}>
              <Box sx={{ display: "flex", alignItems: "baseline", gap: 0.75, mb: 0.75 }}>
                <Tooltip title={getFirstQuestion(conv.messages)} placement="top">
                  <Typography
                    variant="body2"
                    sx={{ fontWeight: 600, color: "#1A1A1A", flex: 1, lineHeight: 1.4, fontSize: "0.925rem" }}
                  >
                    {title}
                  </Typography>
                </Tooltip>
                <Typography variant="caption" sx={{ color: "#bbb", whiteSpace: "nowrap", flexShrink: 0, fontSize: "0.72rem" }}>
                  {relativeDate(conv.last_activity)}
                </Typography>
              </Box>

              {preview && (
                <Typography variant="caption" sx={{ color: "#777", display: "block", mb: 0.75, lineHeight: 1.5, fontSize: "0.78rem" }}>
                  {preview}
                </Typography>
              )}

              <Stack direction="row" spacing={0.75} alignItems="center" flexWrap="wrap">
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.4 }}>
                  <ChatBubbleOutline sx={{ fontSize: 12, color: "#777" }} />
                  <Typography variant="caption" sx={{ color: "#777" }}>
                    {conv.message_count} {conv.message_count === 1 ? "message" : "messages"}
                  </Typography>
                </Box>
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.4 }}>
                  <AccessTime sx={{ fontSize: 12, color: "#777" }} />
                  <Typography variant="caption" sx={{ color: "#777" }}>
                    {getReadTimeFromMessages(conv.messages)}
                  </Typography>
                </Box>
                {conv.asset_class?.map((asset) => (
                  <Chip
                    key={asset}
                    label={asset}
                    size="small"
                    variant="outlined"
                    sx={{ fontSize: "0.6rem", height: 16, color: "#777", borderColor: "#ccc" }}
                  />
                ))}
              </Stack>
            </CardContent>

            <Divider />

            <CardActions sx={{ px: 2, py: 1, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <Typography variant="caption" sx={{ color: "#bbb" }}>
                Started {relativeDate(getStartedDate(conv), false)}
              </Typography>

              <Box sx={{ display: "flex", gap: 1 }}>
                <Tooltip title="Ask this question again in a brand-new chat" arrow placement="top">
                  <Button
                    size="small"
                    variant="outlined"
                    startIcon={<Replay sx={{ fontSize: 13 }} />}
                    onClick={() => onRerunQuestion(getFirstQuestion(conv.messages))}
                    sx={{
                      textTransform: "none",
                      fontSize: "0.72rem",
                      fontWeight: 500,
                      color: "#555",
                      borderColor: "#d0d0d0",
                      borderRadius: 1.5,
                      px: 1.5,
                      py: 0.4,
                      bgcolor: "#fafafa",
                      "&:hover": {
                        bgcolor: "#f0f0f0",
                        borderColor: "#999",
                        color: "#333",
                      },
                    }}
                  >
                    Rerun
                  </Button>
                </Tooltip>

                <Tooltip title="Resume this conversation from where you left off" arrow placement="top">
                  <Button
                    size="small"
                    variant="outlined"
                    endIcon={<PlayArrow sx={{ fontSize: 13 }} />}
                    onClick={() => onOpenConversation(conv)}
                    sx={{
                      textTransform: "none",
                      fontSize: "0.72rem",
                      fontWeight: 500,
                      color: "#CA2920",
                      borderColor: "#CA2920",
                      borderRadius: 1.5,
                      px: 1.5,
                      py: 0.4,
                      "&:hover": { bgcolor: "rgba(202,36,32,0.06)", borderColor: "#A01D1A" },
                    }}
                  >
                    Continue
                  </Button>
                </Tooltip>
              </Box>
            </CardActions>
          </Card>
        );
      })}
    </Box>
  );
}